import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  ArrowLeft, 
  Mail, 
  MessageCircle, 
  Settings, 
  Briefcase,
  Phone,
  Clock,
  CheckCircle,
  MapPin
} from 'lucide-react';

const CompliancePage = ({ pageData, showBackButton = true }) => {
  if (!pageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Page Not Found</h1>
          <p className="text-gray-600 mb-4">The requested page could not be found.</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { title, content } = pageData;

  const getIconComponent = (iconName) => {
    const icons = {
      'message-circle': MessageCircle,
      'mail': Mail,
      'settings': Settings,
      'briefcase': Briefcase,
      'phone': Phone,
      'clock': Clock,
      'check-circle': CheckCircle,
      'map-pin': MapPin
    };
    return icons[iconName] || Mail;
  };

  const renderSection = (section, index) => {
    const isArray = Array.isArray(section.content);
    
    return (
      <div key={index} className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
        {isArray ? (
          <div className="space-y-3">
            {section.content.map((item, itemIndex) => (
              <div key={itemIndex} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-700 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-700 leading-relaxed">{section.content}</p>
        )}
      </div>
    );
  };

  const renderContactMethods = (contactMethods) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {contactMethods.map((method, index) => {
          const IconComponent = getIconComponent(method.icon);
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <IconComponent className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{method.title}</CardTitle>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <a
                    href={method.link}
                    target={method.link.startsWith('http') ? '_blank' : '_self'}
                    rel={method.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {method.value}
                  </a>
                  <p className="text-sm text-gray-500 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {method.available}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderFeatures = (features) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((feature, index) => (
          <Card key={index} className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="EventEase" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex space-x-4">
              {showBackButton && (
                <Link to="/">
                  <Button variant="outline" className="flex items-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {content.hero.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {content.hero.subtitle}
          </p>
          {content.lastUpdated && (
            <p className="text-sm text-blue-200 mt-4">
              Last updated: {content.lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          {/* Sections */}
          {content.sections && content.sections.map((section, index) => renderSection(section, index))}
          
          {/* Contact Methods (for Contact Us page) */}
          {content.contactMethods && (
            <>
              <div className="border-t pt-8 mt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Methods</h2>
                {renderContactMethods(content.contactMethods)}
              </div>
            </>
          )}
          
          {/* Features (for Contact Us page) */}
          {content.features && (
            <>
              <div className="border-t pt-8 mt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Our Support</h2>
                {renderFeatures(content.features)}
              </div>
            </>
          )}
          
          {/* Contact Section */}
          {content.contact && (
            <div className="border-t pt-8 mt-8">
              <Card className="bg-gray-50">
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    {content.contact.title}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {content.contact.content}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {content.contact.email && (
                      <a
                        href={`mailto:${content.contact.email}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-700"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        {content.contact.email}
                      </a>
                    )}
                    {content.contact.whatsapp && (
                      <a
                        href={`https://wa.me/${content.contact.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-green-600 hover:text-green-700"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        WhatsApp Support
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <p>&copy; {new Date().getFullYear()} NextWave SaaS OPC Private Limited. All rights reserved.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/about-us" className="hover:text-gray-300">About Us</Link>
              <Link to="/privacy-policy" className="hover:text-gray-300">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-gray-300">Terms of Service</Link>
              <Link to="/refund-policy" className="hover:text-gray-300">Refund Policy</Link>
              <Link to="/shipping-policy" className="hover:text-gray-300">Shipping &amp; Delivery</Link>
              <Link to="/contact-us" className="hover:text-gray-300">Contact Us</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompliancePage;
